import { useState, useEffect } from 'react';
import { Card, CardContent, Button, Input } from "@repo/o37-ui";
import {
  Link2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  CreditCard,
  Wallet,
  PiggyBank,
  Building2,
  Eye,
  EyeOff,
  Download,
  ArrowRight
} from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface MercuryAccount {
  id: string;
  mercuryId: string;
  name: string;
  type: string;
  balance: number;
  lastFour: string | null;
}

interface SyncResult {
  accountId: string;
  name: string;
  imported: number;
  matched: number;
  error?: string;
}

export default function MercurySetup() {
  const [step, setStep] = useState<'connect' | 'accounts' | 'sync' | 'done'>('connect');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Connected accounts
  const [connectedAccounts, setConnectedAccounts] = useState<MercuryAccount[]>([]);
  const [existingAccounts, setExistingAccounts] = useState<any[]>([]);

  // Sync results
  const [syncResults, setSyncResults] = useState<SyncResult[]>([]);
  const [syncSummary, setSyncSummary] = useState<{ totalImported: number; totalMatched: number } | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadExistingAccounts();
  }, []);

  const loadExistingAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/holdings/bank-accounts`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (res.ok) {
        const data = await res.json();
        setExistingAccounts(data.filter((a: any) => a.provider === 'mercury'));
        // If already connected, start at accounts step
        if (data.some((a: any) => a.provider === 'mercury')) {
          setStep('accounts');
        }
      }
    } catch (e) {
      console.error('Failed to load accounts:', e);
    }
  };

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your Mercury API key');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/holdings/mercury/auto-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ apiKey: apiKey.trim() })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to connect to Mercury');
        return;
      }

      setConnectedAccounts(data.accounts || []);
      await loadExistingAccounts();
      setStep('accounts');
    } catch (e: any) {
      setError(e.message || 'Failed to connect');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    setLoading(true);
    setError('');
    setSyncResults([]);
    setStep('sync');

    try {
      const res = await fetch(`${API_URL}/holdings/mercury/sync-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        }
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Sync failed');
        return;
      }

      setSyncResults(data.accounts || []);
      setSyncSummary({ totalImported: data.totalImported, totalMatched: data.totalMatched });
      setStep('done');
    } catch (e: any) {
      setError(e.message || 'Sync failed');
    } finally {
      setLoading(false);
    }
  };

  const getAccountIcon = (type: string) => {
    switch (type) {
      case 'checking':
        return <Wallet className="w-5 h-5" />;
      case 'savings':
        return <PiggyBank className="w-5 h-5" />;
      case 'credit':
      case 'credit_card':
        return <CreditCard className="w-5 h-5" />;
      default:
        return <Building2 className="w-5 h-5" />;
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold mb-2">Mercury Integration</h1>
        <p className="text-neutral-500">
          Connect your Mercury accounts to automatically sync all transactions
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 mb-8">
        {['connect', 'accounts', 'sync', 'done'].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === s ? 'bg-blue-600 text-white' :
              ['connect', 'accounts', 'sync', 'done'].indexOf(step) > i ? 'bg-emerald-500 text-white' :
              'bg-neutral-200 text-neutral-500'
            }`}>
              {['connect', 'accounts', 'sync', 'done'].indexOf(step) > i ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                i + 1
              )}
            </div>
            {i < 3 && <div className={`w-12 h-0.5 ${['connect', 'accounts', 'sync', 'done'].indexOf(step) > i ? 'bg-emerald-500' : 'bg-neutral-200'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Connect */}
      {step === 'connect' && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Link2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Connect Mercury</h2>
                <p className="text-sm text-neutral-500">Enter your Mercury API key to get started</p>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg mb-4 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Mercury API Key
                </label>
                <div className="relative">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Enter your Mercury API key"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  <a
                    href="https://app.mercury.com/settings/tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Create a read-only API token
                  </a>
                  {' '}in Mercury Settings &rarr; API Tokens
                </p>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg space-y-2">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Your API key will be securely stored using Supabase Vault encryption.
                  One API key gives access to all your Mercury bank accounts (checking, savings).
                </p>
                <p className="text-sm text-amber-700">
                  <strong>Note:</strong> Mercury's API does not include credit cards (IO Card). To track IO Card transactions,
                  add it manually as a "Credit Card" account and import transactions via CSV export from Mercury.
                </p>
              </div>

              <Button
                onClick={handleConnect}
                disabled={loading || !apiKey.trim()}
                icon={loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                text={loading ? 'Connecting...' : 'Connect & Discover Accounts'}
                className="w-full"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Accounts */}
      {step === 'accounts' && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Accounts Connected</h2>
                <p className="text-sm text-neutral-500">
                  {existingAccounts.length} Mercury account{existingAccounts.length !== 1 ? 's' : ''} ready to sync
                </p>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg mb-4 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Show newly connected accounts */}
            {connectedAccounts.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-emerald-600 mb-2">
                  Just added {connectedAccounts.length} account{connectedAccounts.length !== 1 ? 's' : ''}:
                </p>
                <div className="space-y-2">
                  {connectedAccounts.map(acc => (
                    <div key={acc.id} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                      <div className="text-emerald-600">{getAccountIcon(acc.type)}</div>
                      <div className="flex-1">
                        <div className="font-medium text-neutral-900">{acc.name}</div>
                        <div className="text-xs text-neutral-500">
                          {acc.type} {acc.lastFour ? `(${acc.lastFour})` : ''}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-medium ${acc.balance < 0 ? 'text-red-600' : 'text-neutral-900'}`}>
                          {formatCurrency(acc.balance * 100)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Show all connected accounts */}
            <div className="mb-6">
              <p className="text-sm font-medium text-neutral-700 mb-2">All Mercury accounts:</p>
              <div className="space-y-2">
                {existingAccounts.map(acc => (
                  <div key={acc.id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                    <div className="text-neutral-500">{getAccountIcon(acc.account_type)}</div>
                    <div className="flex-1">
                      <div className="font-medium text-neutral-900">{acc.name}</div>
                      <div className="text-xs text-neutral-500">
                        {acc.account_type} {acc.last_four ? `(****${acc.last_four})` : ''}
                        {acc.last_synced_at && (
                          <span className="ml-2">
                            Last synced: {new Date(acc.last_synced_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg mb-4 space-y-2">
              <p className="text-sm text-blue-800">
                <strong>Ready to sync:</strong> This will import transactions from the last 12 months
                from all connected accounts. Duplicates are automatically detected and skipped.
              </p>
              <p className="text-sm text-blue-700">
                <strong>IO Credit Card:</strong> Mercury's API doesn't include credit cards. Add your IO Card
                manually via Bank Import → Add Account → Credit Card, then import CSV statements.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep('connect')}
                text="Add Another API Key"
              />
              <Button
                onClick={handleSyncAll}
                disabled={loading}
                icon={loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                text={loading ? 'Syncing...' : 'Sync All Transactions'}
                className="flex-1"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Syncing */}
      {step === 'sync' && (
        <Card>
          <CardContent className="p-6 text-center">
            <RefreshCw className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-lg font-semibold mb-2">Syncing Transactions</h2>
            <p className="text-neutral-500">
              Importing transactions from all Mercury accounts...
            </p>
            <p className="text-sm text-neutral-400 mt-2">
              This may take a minute for large transaction histories.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Done */}
      {step === 'done' && (
        <Card>
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold mb-1">Sync Complete!</h2>
              {syncSummary && (
                <p className="text-neutral-500">
                  Imported <strong>{syncSummary.totalImported.toLocaleString()}</strong> transactions,
                  {' '}<strong>{syncSummary.totalMatched.toLocaleString()}</strong> automatically matched
                </p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg mb-4 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Per-account results */}
            <div className="space-y-2 mb-6">
              {syncResults.map(result => (
                <div
                  key={result.accountId}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    result.error ? 'bg-red-50' : 'bg-neutral-50'
                  }`}
                >
                  <div className={result.error ? 'text-red-500' : 'text-emerald-500'}>
                    {result.error ? <AlertCircle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-neutral-900">{result.name}</div>
                    {result.error ? (
                      <div className="text-xs text-red-600">{result.error}</div>
                    ) : (
                      <div className="text-xs text-neutral-500">
                        {result.imported} imported, {result.matched} matched
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('accounts');
                  loadExistingAccounts();
                }}
                icon={<RefreshCw className="w-4 h-4" />}
                text="Sync Again"
              />
              <Button
                onClick={() => window.location.href = '/holdings/transactions'}
                icon={<ArrowRight className="w-4 h-4" />}
                text="View Transactions"
                className="flex-1"
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
